import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { normalizeAlertScore } from '../../../../util/normalization';

const PillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  align-items: center;
`;

const Label = styled.span`
  font-size: 12px;
  color: #748aa1;
  margin-right: 4px;
  white-space: nowrap;
`;

const Pill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  border-radius: 12px;
  border: 1px solid ${(props) => (props.$selected ? '#1890ff' : '#d9d9d9')};
  background: ${(props) => (props.$selected ? '#e6f7ff' : '#fff')};
  color: ${(props) => (props.$selected ? '#1890ff' : '#31394d')};
  font-size: 12px;
  cursor: pointer;
  font-weight: ${(props) => (props.$selected ? 600 : 400)};
  transition: all 0.2s;

  &:hover {
    border-color: #1890ff;
    color: #1890ff;
  }
`;

const EmptyState = styled.div`
  padding: 12px 16px;
  font-size: 13px;
  color: #748aa1;
`;

/**
 * AlertSelector — per-tab pill switcher for FP/AML investigation tabs.
 *
 * Props:
 *   alerts  {Array<{ alertId: string, score?: number|string }>}
 *   value   {string}  — currently selected alertId
 *   onChange {Function(alertId: string)} — called when user clicks a pill
 */
// FIN-13184 — the chip and the Score Explanation panel show the SAME alert on DIFFERENT scales,
// and neither said so. The chip renders unified_alerts.ALERT_SCORE; the panel's typology hover
// renders tm_iad_typology_threshold.TYPOLOGY_SCORE. Measured on keycloak-qa, the relationship is
// systematic, not a defect:
//
//   ALERT_SCORE = typology score x 100   (11,259 of 11,259 single-typology alerts, exactly;
//                                         130,208 multi-typology alerts vs MAX(typology))
//
// So 3.3666 and 0.033666 describe the same alert, one as a 0-100 score and one as a 0-1 model
// value. The reported "100x inflated" chip is NOT corrupt data - and rendering the chip as
// 0.033666, as the ticket asks, would make it disagree with every other surface that reads
// ALERT_SCORE (the FRAML queue, alert lists, exports, CM). The real defect is that neither
// surface names its scale.
//
// Two changes here, both presentation-only: render the score at the TM alert-score precision, and
// state the scale on hover.
//
// PRECISION (re-open, FIN-13184 comments 2026-09-21/23): the ticket's Expected asked for the chip
// to read 0.033666. Product answered "working as designed"; QA then asked to "match with TM
// score", and the agreed resolution was "will match the precision". So the chip shows exactly what
// the TM alert page shows for the same ALERT_SCORE - `normalizeAlertScore(score.toFixed(2))`, the
// expression used by CM AlertDetails ("Alert Score" card) and the CM alert list (AlertScore.js):
// 2 decimals kept ("3.30", "50.00"), clamped to 0..100. The earlier "trim trailing zeros" format
// ("3.3", "50") differed from TM for any score whose 2nd decimal is 0. An empty or non-numeric
// score renders no score at all rather than a misleading "(0)". No "%" suffix: this
// component is shared by the FRAUD tab too, whose scores are natively 0-100 rather than a scaled
// probability, so a percent sign would be wrong there.
// The hint DEPENDS ON THE TAB, because this component renders on both and the panel beneath it
// is not the same thing in each (tabAdapters.js):
//
//   AML Investigation  -> ScoreExplainability isPrevention={false} -> typology model values,
//                         0-1. The chip is that quantity x100, which is the confusion the
//                         ticket reports, so here it is worth saying so explicitly.
//   FP Investigation   -> ScoreExplainability isPrevention={true}  -> action and rule scores.
//                         NOT 0-1 model values. Claiming otherwise would be a false statement
//                         of scale on the very screen this change exists to make legible —
//                         caught in review on #11710.
//
// Fraud scores are natively 0-100, so that tab gets the scale named and nothing more.
// Worded as the MAX relation because that is what holds on every alert: on a single-typology
// alert the chip is that typology x100; on a multi-typology alert it is the HIGHEST typology
// x100, not each row's (measured on keycloak-qa, see above). "The same quantity, x100" was only
// true of the first case - review on #11710.
const SCORE_SCALE_HINT_AML = [
  'Alert score on a 0-100 scale.',
  'The Score Explanation panel below shows typology model values on a 0-1 scale;',
  'the alert score is the highest typology value x100.'
].join(' ');

const SCORE_SCALE_HINT_FRAUD = 'Alert score on a 0-100 scale.';

export const scoreScaleHint = (isPrevention) =>
  isPrevention ? SCORE_SCALE_HINT_FRAUD : SCORE_SCALE_HINT_AML;

// Returns the chip text, or null when there is no displayable score (null, '', NaN, 'n/a').
// Number('') is 0, so the empty string is rejected explicitly - it used to render "(0)".
export const formatScore = (score) => {
  if (score == null || (typeof score === 'string' && score.trim() === '')) {
    return null;
  }
  const n = Number(score);
  if (!Number.isFinite(n)) return null;
  // Same expression as the TM alert page (AlertDetails.js / AlertScore.js).
  return String(normalizeAlertScore(n.toFixed(2)));
};

const AlertSelector = ({ alerts, value, onChange, isPrevention = false }) => {
  if (!alerts || alerts.length === 0) {
    return <EmptyState data-testid="alert-selector-empty">No alerts</EmptyState>;
  }

  return (
    <PillRow data-testid="alert-selector">
      <Label>Alert:</Label>
      {alerts.map((alert) => {
        const id = alert.alertId;
        const selected = id === value;
        const scoreText = formatScore(alert.score);
        return (
          <Pill
            key={id}
            $selected={selected}
            data-testid={`alert-pill-${id}`}
            aria-pressed={selected}
            onClick={() => onChange(id)}>
            <span data-testid={`alert-pill-id-${id}`}>{id}</span>
            {scoreText != null && (
              <span
                data-testid={`alert-pill-score-${id}`}
                title={scoreScaleHint(isPrevention)}>
                ({scoreText})
              </span>
            )}
          </Pill>
        );
      })}
    </PillRow>
  );
};

AlertSelector.propTypes = {
  alerts: PropTypes.arrayOf(
    PropTypes.shape({
      alertId: PropTypes.string.isRequired,
      score: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    })
  ),
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  // FIN-13184 — which tab this is on. Drives the score hint, because the panel below differs.
  isPrevention: PropTypes.bool
};

export default AlertSelector;
